import { get, merge, } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberInfoSettingPaymentInfo defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberInfoSettingPaymentInfoService } from 'services/memberInfoSettingPaymentInfo';
 *
 * // create service instance and send/cancel request
 * const memberInfoSettingPaymentInfoService = new MemberInfoSettingPaymentInfoService();
 * const result = await memberInfoSettingPaymentInfoService.fetchData( '<user-id>' ); // fetch data
 * memberInfoSettingPaymentInfoService.cancel(); // cancel an active request
 */
export class MemberInfoSettingPaymentInfoService extends HttpService {

    // fetch call control widget data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        ...data,
                        callId,
                        callType,
                        callerType
                    }
                }
            } );

            // extract data
            const visibility = get( response, 'body.component.visibility', {} );
            const modifyActions = get( response, 'body.component.structure.modifyActions', {} );
            const addAction = get( response, 'body.component.structure.addAction', {} );
            const component = get( response, 'body.component', {} );
            
            if( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    data: {
                        options: get( response, 'body.component.data.options', {} ),
                        addAction: isVisible( addAction, visibility ) ? addAction: {},
                        modifyActions: getVisibleItems( modifyActions, visibility ),
                        changeConfirmation: get( component, 'structure.widgetConfig.changeConfirmation', {} ),
                        defaultDescription: isVisible( get( component, 'structure.widgetConfig.defaultDescription', {} ), visibility ) ? get( response, 'body.component.structure.widgetConfig.defaultDescription', {} ): {},
                        ...get( response, 'body.component.data.state', {} )
                    },
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: get( response, 'body.component.data', {} )
                };
            }
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async setDefaultPaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // make request and fetch data
    async deletePaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // make request and fetch data
    async submitEditedPaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        operation: CRUD_OPERATION.UPDATE,
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // submits payment card activation request
    async submitActivatePaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // submits payment card activation request
    async submitToggleHsaFsaPaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }
}
